<template>
  <div>
    <div class="main-content fe-account-page fe-order-details">
      <div class="container py-4 py-lg-5">
        <header class="fe-account-page__header">
          <h1 class="fe-account-page__title">Order details</h1>
          <p class="fe-account-page__subtitle">
            Review items, pricing, and status for this order.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div class="col-lg-9 col-md-8">
            <div v-if="loading" class="fe-order-details__state">
              <div class="fe-order-details__spinner" aria-hidden="true"></div>
              <p>Loading order details…</p>
            </div>

            <div v-else-if="error" class="fe-order-details__error" role="alert">
              <i class="fas fa-exclamation-circle"></i>
              {{ error }}
            </div>

            <template v-else-if="order">
              <section class="fe-account-card fe-order-summary-card">
                <div class="fe-order-summary-card__top">
                  <div>
                    <p class="fe-order-summary-card__label">Order reference</p>
                    <p class="fe-order-summary-card__ref">{{ order.reference }}</p>
                  </div>
                  <span class="fe-order-status" :class="statusClass">
                    {{ displayStatus }}
                  </span>
                </div>
                <dl class="fe-order-meta">
                  <div class="fe-order-meta__row">
                    <dt>Date</dt>
                    <dd>{{ formatDate(order.system_date) }}</dd>
                  </div>
                  <div class="fe-order-meta__row">
                    <dt>Total</dt>
                    <dd class="fe-order-meta__total">LKR {{ formatMoney(order.total_order_value) }}</dd>
                  </div>
                </dl>
              </section>

              <section class="fe-account-card fe-order-items-card">
                <div class="fe-account-card__head">
                  <h2 class="fe-account-card__title">Items</h2>
                  <span class="fe-account-card__tag">{{ orderItems.length }} item(s)</span>
                </div>

                <div v-if="orderItems.length === 0" class="fe-order-details__empty">
                  No line items were returned for this order.
                </div>

                <div v-else class="table-responsive fe-orders-table-wrap">
                  <table class="table fe-orders-table fe-order-line-table mb-0">
                    <thead>
                      <tr>
                        <th class="fe-order-line-table__img">Image</th>
                        <th>Product</th>
                        <th>Unit price</th>
                        <th>Qty</th>
                        <th class="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in orderItems" :key="item.id">
                        <td class="fe-order-line-table__img">
                          <div class="fe-order-thumb">
                            <img
                              v-if="lineImageSrc(item)"
                              :src="lineImageSrc(item)"
                              :alt="item.product_name || 'Product'"
                              loading="lazy"
                              @error="onLineImageError(item)"
                            />
                            <span v-else class="fe-order-thumb__ph" aria-hidden="true">
                              <i class="fas fa-image"></i>
                            </span>
                          </div>
                        </td>
                        <td data-label="Product">{{ item.product_name }}</td>
                        <td data-label="Unit price">
                          <span class="fe-order-money">LKR {{ formatMoney(item.product_price) }}</span>
                        </td>
                        <td data-label="Qty">{{ formatMoney(item.product_qty) }}</td>
                        <td data-label="Total" class="text-end">
                          <span class="fe-order-money fe-order-money--strong">
                            LKR {{ formatMoney(item.total_gross_value) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section v-if="tracking" class="fe-account-card fe-order-tracking-section">
                <div class="fe-account-card__head">
                  <h2 class="fe-account-card__title">
                    <i class="fas fa-route" style="color: var(--fe-accent, #fcbe00); margin-right: 0.4rem;"></i>
                    Order Tracking
                  </h2>
                  <router-link
                    :to="'/order-tracking?id=' + id"
                    class="fe-order-track-link"
                  >
                    View full tracking <i class="fas fa-external-link-alt"></i>
                  </router-link>
                </div>
                <div class="fe-order-inline-timeline">
                  <div
                    v-for="(step, idx) in tracking.timeline"
                    :key="step.key"
                    class="fe-order-inline-step"
                    :class="{
                      'is-completed': step.status === 'completed',
                      'is-active': step.status === 'in_progress',
                      'is-failed': step.status === 'failed',
                    }"
                  >
                    <div class="fe-order-inline-step__dot">
                      <i v-if="step.status === 'completed'" class="fas fa-check"></i>
                      <i v-else-if="step.status === 'failed'" class="fas fa-times"></i>
                      <i v-else-if="step.status === 'in_progress'" class="fas fa-spinner fa-pulse"></i>
                      <span v-else>{{ step.step }}</span>
                    </div>
                    <div
                      v-if="idx < tracking.timeline.length - 1"
                      class="fe-order-inline-step__line"
                      :class="{ 'is-filled': step.status === 'completed' && tracking.timeline[idx + 1].status !== 'pending' }"
                    ></div>
                    <span class="fe-order-inline-step__label">{{ step.title }}</span>
                  </div>
                </div>
              </section>

              <section class="fe-order-actions-row">
                <div class="fe-account-card fe-order-totals-card">
                  <h3 class="fe-order-totals-card__title">Order total</h3>
                  <div class="fe-order-totals-card__line">
                    <span>Grand total</span>
                    <span class="fe-order-totals-card__amount">
                      LKR {{ formatMoney(order.total_order_value) }}
                    </span>
                  </div>
                </div>
                <router-link to="/my-orders" class="fe-order-back-btn">
                  <i class="fas fa-arrow-left"></i>
                  Back to orders
                </router-link>
              </section>
            </template>
          </div>
        </div>
      </div>
    </div>

    <cta-area />
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'
import { resolveAdImageUrl, withAbsoluteMediaOrigin } from '@/utils/ad-media'

export default {
  name: 'OrderDetails',
  components: {
    CtaArea,
    AccountSidebar,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      order: null,
      orderItems: [],
      loading: true,
      error: null,
      failedLineImageIds: [],
      tracking: null,
    }
  },
  computed: {
    rawStatus() {
      return (this.order && this.order.order_approval_status) || ''
    },
    displayStatus() {
      const s = this.rawStatus.trim()
      if (/^comfirmed$/i.test(s)) return 'Confirmed'
      return s || '—'
    },
    statusClass() {
      const s = this.displayStatus.toLowerCase()
      if (s.includes('confirm') || s.includes('complete') || s.includes('delivered')) {
        return 'fe-order-status--success'
      }
      if (s.includes('pending') || s.includes('process')) {
        return 'fe-order-status--warn'
      }
      if (s.includes('cancel')) {
        return 'fe-order-status--danger'
      }
      return 'fe-order-status--neutral'
    },
  },
  methods: {
    formatDate(date) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(date).toLocaleDateString(undefined, options)
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    /** Root-relative `/storage/...` so Vue devServer proxy (:8080 → Laravel) loads files. */
    lineImageSrc(item) {
      const id = item && item.id
      if (id != null && this.failedLineImageIds.includes(id)) {
        return ''
      }
      const raw = item && item.image_url != null ? String(item.image_url).trim() : ''
      if (!raw) return ''
      // Root-relative `/storage/...` must hit Laravel (:8000), not only the Vue dev proxy.
      return withAbsoluteMediaOrigin(resolveAdImageUrl(raw))
    },
    onLineImageError(item) {
      const id = item && item.id
      if (id == null || this.failedLineImageIds.includes(id)) return
      this.failedLineImageIds = [...this.failedLineImageIds, id]
    },
    async loadOrderData() {
      this.loading = true
      this.error = null
      this.failedLineImageIds = []
      try {
        const [orderRes, itemsRes] = await Promise.all([
          this.$axios.get(`/orders/${this.id}`),
          this.$axios.get(`/order-items/by-order/${this.id}`),
        ])
        this.order = orderRes.data
        this.orderItems = Array.isArray(itemsRes.data) ? itemsRes.data : []
        this.loadTracking()
      } catch {
        this.error = 'Failed to load order details. Please try again later.'
        this.order = null
        this.orderItems = []
      } finally {
        this.loading = false
      }
    },
    async loadTracking() {
      try {
        const { data } = await this.$axios.get('/track-order', {
          params: { order_id: this.id },
        })
        this.tracking = data
      } catch {
        this.tracking = null
      }
    },
  },
  mounted() {
    this.loadOrderData()
  },
}
</script>

<style scoped>
.fe-account-page {
  background: linear-gradient(180deg, #f8f8f8 0%, #fff 120px);
}

.fe-account-page__header {
  text-align: center;
  margin-bottom: 2rem;
}

.fe-account-page__title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #141414;
  margin: 0 0 0.5rem;
}

.fe-account-page__subtitle {
  margin: 0 auto;
  max-width: 520px;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.5;
}

.fe-account-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ececec;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
  padding: 1.35rem 1.5rem 1.5rem;
  margin-bottom: 1.25rem;
}

.fe-account-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #eee;
}

.fe-account-card__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #141414;
}

.fe-account-card__tag {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 16%, #fff);
  color: #5c4a00;
}

.fe-order-summary-card__top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.fe-order-summary-card__label {
  margin: 0 0 0.25rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #888;
}

.fe-order-summary-card__ref {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #141414;
  word-break: break-all;
}

.fe-order-status {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fe-order-status--success {
  background: #e8f5e9;
  color: #1b5e20;
}

.fe-order-status--warn {
  background: #fff8e1;
  color: #f57f17;
}

.fe-order-status--danger {
  background: #ffebee;
  color: #c62828;
}

.fe-order-status--neutral {
  background: #f5f5f5;
  color: #424242;
}

.fe-order-meta {
  margin: 0;
  display: grid;
  gap: 0.75rem;
}

.fe-order-meta__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 0;
  border-top: 1px solid #f0f0f0;
}

.fe-order-meta__row:first-of-type {
  border-top: none;
  padding-top: 0;
}

.fe-order-meta dt {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #777;
}

.fe-order-meta dd {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #222;
}

.fe-order-meta__total {
  font-size: 1.05rem !important;
  font-weight: 800 !important;
  color: #111 !important;
}

.fe-orders-table-wrap {
  border-radius: 12px;
  border: 1px solid #eee;
  overflow: hidden;
}

.fe-orders-table {
  margin: 0;
  font-size: 0.9rem;
}

.fe-orders-table thead th {
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #666;
  white-space: nowrap;
  padding: 0.75rem 1rem;
}

.fe-orders-table tbody td {
  padding: 0.85rem 1rem;
  vertical-align: middle;
  border-color: #f0f0f0;
  color: #333;
}

.fe-order-line-table__img {
  width: 88px;
}

.fe-order-thumb {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fe-order-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fe-order-thumb__ph {
  color: #bbb;
  font-size: 1.25rem;
}

.fe-order-money {
  font-variant-numeric: tabular-nums;
}

.fe-order-money--strong {
  font-weight: 800;
  color: #111;
}

.fe-order-details__empty {
  padding: 1rem 0;
  color: #777;
  font-size: 0.95rem;
}

.fe-order-details__state {
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
}

.fe-order-details__spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid #eee;
  border-top-color: var(--fe-accent, #fcbe00);
  border-radius: 50%;
  animation: fe-spin 0.85s linear infinite;
}

@keyframes fe-spin {
  to {
    transform: rotate(360deg);
  }
}

.fe-order-details__error {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 1.15rem;
  border-radius: 12px;
  background: #fff5f5;
  border: 1px solid #ffcdd2;
  color: #b71c1c;
  font-weight: 600;
}

.fe-order-actions-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 1rem;
  justify-content: space-between;
}

.fe-order-totals-card {
  flex: 1 1 240px;
  margin-bottom: 0 !important;
  padding: 1.15rem 1.35rem !important;
}

.fe-order-totals-card__title {
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #141414;
}

.fe-order-totals-card__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9rem;
  color: #555;
}

.fe-order-totals-card__amount {
  font-size: 1.2rem;
  font-weight: 800;
  color: #111;
  font-variant-numeric: tabular-nums;
}

.fe-order-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  align-self: center;
  padding: 0.65rem 1.25rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  text-decoration: none;
  color: #1a1a1a;
  border: 1px solid color-mix(in srgb, var(--fe-accent, #fcbe00) 45%, #ddd);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--fe-accent, #fcbe00) 10%, #fff) 0%,
    #fff 100%
  );
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
}

.fe-order-back-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  color: #111;
}

/* ─── Inline Order Tracking ─── */
.fe-order-tracking-section {
  margin-bottom: 1.25rem;
}

.fe-order-track-link {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--fe-accent, #d4a000);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.fe-order-track-link:hover {
  text-decoration: underline;
}

.fe-order-inline-timeline {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  padding: 0.5rem 0;
  position: relative;
}

.fe-order-inline-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;
}

.fe-order-inline-step__dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  background: #e2e8f0;
  color: #94a3b8;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

.fe-order-inline-step.is-completed .fe-order-inline-step__dot {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(34, 197, 94, 0.3);
}

.fe-order-inline-step.is-active .fe-order-inline-step__dot {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
  animation: fe-inline-pulse 2s ease-in-out infinite;
}

.fe-order-inline-step.is-failed .fe-order-inline-step__dot {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(239, 68, 68, 0.3);
}

@keyframes fe-inline-pulse {
  0%, 100% { box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 3px 18px rgba(59, 130, 246, 0.5); }
}

.fe-order-inline-step__line {
  position: absolute;
  top: 18px;
  left: calc(50% + 18px);
  right: calc(-50% + 18px);
  height: 3px;
  background: #e2e8f0;
  z-index: 1;
  border-radius: 999px;
}

.fe-order-inline-step__line.is-filled {
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
}

.fe-order-inline-step__label {
  margin-top: 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #94a3b8;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.fe-order-inline-step.is-completed .fe-order-inline-step__label {
  color: #16a34a;
}

.fe-order-inline-step.is-active .fe-order-inline-step__label {
  color: #2563eb;
}

.fe-order-inline-step.is-failed .fe-order-inline-step__label {
  color: #dc2626;
}

@media (max-width: 767.98px) {
  .fe-order-inline-step__label {
    font-size: 0.6rem;
  }

  .fe-order-inline-step__dot {
    width: 28px;
    height: 28px;
    font-size: 10px;
  }

  .fe-order-inline-step__line {
    top: 14px;
    left: calc(50% + 14px);
    right: calc(-50% + 14px);
    height: 2px;
  }
}

@media (max-width: 767.98px) {
  .fe-orders-table.fe-order-line-table thead {
    display: none;
  }

  .fe-orders-table.fe-order-line-table tbody tr {
    display: block;
    border-bottom: 1px solid #eee;
    padding: 0.75rem 0;
  }

  .fe-orders-table.fe-order-line-table tbody td {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    border: none;
    padding: 0.35rem 0.75rem;
  }

  .fe-orders-table.fe-order-line-table tbody td::before {
    content: attr(data-label);
    font-weight: 700;
    color: #888;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .fe-orders-table.fe-order-line-table tbody td.fe-order-line-table__img {
    justify-content: center;
  }

  .fe-orders-table.fe-order-line-table tbody td.fe-order-line-table__img::before {
    display: none;
  }

  .fe-orders-table.fe-order-line-table tbody td.text-end {
    justify-content: flex-end;
  }

  .fe-orders-table.fe-order-line-table tbody td.text-end::before {
    display: none;
  }

  .fe-order-actions-row {
    flex-direction: column;
  }

  .fe-order-back-btn {
    width: 100%;
  }
}
</style>